import { parseDrawerChain } from "shared-ui/src/components/detail-drawer/drawer-stack";
import { ORDER_DRAWER_ROUTES } from "@features/orders-features/components/order-drawer/orderDrawerRoutes";
import { stripGlobalDialogSegment } from "@features/ecommerce/constants/cartRouteSegments";

const decodePart = (part: string): string => {
  try {
    return decodeURIComponent(part);
  } catch {
    return part;
  }
};

const countParts = (path: string): number =>
  path.split("/").filter(Boolean).length;

/**
 * Whether the `remainder` matched by a host route's `/*` is empty or lies within a drawer
 * chain, so `/cloud/order/so-1` keeps the host page and `/cloud/typo` does not. The chain
 * may start in a route param, as in `/projects/:projectId/:tabId?/*` without a tab. A trailing
 * global dialog segment such as `added-to-cart` is accepted on top of either.
 */
export const isDrawerChainRemainder = (
  pathname: string,
  remainder: string | undefined,
  pathPrefix?: string,
  drawerBasePath?: string,
): boolean => {
  const fullRemainder = remainder?.replace(/^\/+|\/+$/g, "") ?? "";
  const trimmedRemainder = stripGlobalDialogSegment(fullRemainder);
  if (!trimmedRemainder) {
    return true;
  }
  const hostPathname =
    trimmedRemainder === fullRemainder ? pathname : stripGlobalDialogSegment(pathname);
  const chainPath = drawerBasePath
    ? hostPathname.slice(drawerBasePath.length)
    : hostPathname;
  const chain = parseDrawerChain(
    chainPath,
    ORDER_DRAWER_ROUTES,
    undefined,
    drawerBasePath ? undefined : pathPrefix,
  );
  if (drawerBasePath && chain.basePath !== "/") {
    return false;
  }
  if (chain.levels.length === 0) {
    return false;
  }
  // The remainder is decoded, so compare decoded text rather than counting encoded parts.
  const chainText = chainPath
    .split("/")
    .filter(Boolean)
    .slice(countParts(chain.basePath))
    .map(decodePart)
    .join("/");
  return chainText.length >= trimmedRemainder.length;
};
